import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
    VistarAPIFetchAdsResponse,
    VistarAd,
    VistarError,
    vistarApiFetchAdsResponseSchema,
} from './types';
import { Log } from '../../../AuxillaryService/Log';
import { Signage } from '../../../Signage';
import AdQueue from '../AdQueue';
import { ZodError } from 'zod';

/**
 * A class that contains a few helper
 * functions to execute various http req
 * to Vistar's API
 */
export class VistarApiHelper {
    /**
     * fetches ads from the Vistar API
     * & enqueues fetched ads to the vistar Ad
     * queue.
     * @see {@link https://help.vistarmedia.com/hc/en-us/articles/225058628-Ad-Serving-API#adreq Vistar API - To request an ad}
     */
    static async fetchAds(
        networkId: string,
        apiKey: string,
        venueId: string,
        width: number,
        height: number,
        displayTime: number,
        numberOfAds: number,
        playerUuid: string,
        maxDuration: number,
        adQueue: AdQueue<VistarAd>
    ): Promise<void> {
        Log.Info(
            `Vistar: fetching ads ${numberOfAds} for Venue ${venueId} for network  ${networkId}`,
            null,
            __fileline
        );

        const computedMinDuration: number = Math.max(1, maxDuration - 5);
        const options: AxiosRequestConfig = {
            method: 'POST',
            url: 'https://api.vistarmedia.com/api/v1/get_ad/json',
            headers: {
                'Content-Type': 'application/json',
            },
            data: {
                device_id: playerUuid,
                network_id: networkId,
                api_key: apiKey,
                direct_connection: false,
                device_attribute: [],
                display_area: [
                    {
                        supported_media: [
                            'image/jpeg',
                            'image/png',
                            'video/mp4',
                            'video/mpeg',
                            'video/mpg',
                            'video/webm',
                        ],
                        height: Number(height),
                        width: Number(width),
                        allow_audio: true,
                        id: playerUuid,
                        static_duration: Number(maxDuration),
                        min_duration: computedMinDuration,
                        max_duration: Number(maxDuration),
                    },
                ],
                venue_id: venueId,
                display_time: displayTime,
            },
        };
        for (let i = 1; i <= numberOfAds; i++) {
            let response: AxiosResponse;
            try {
                response = await axios.request(options);
            } catch (error: unknown) {
                const errorMessage = this.isVistarError(error)
                    ? error.response?.data.error
                    : String(error);

                Log.Error(`Vistar: Failed to fetch ads: ${errorMessage}`, null, __fileline);
                return;
            }

            if (this.isResponseEmpty(response.data)) {
                Log.Info(
                    `Vistar: no new content available to play on unit:  ${playerUuid}  with VenueId ${venueId}`,
                    null,
                    __fileline
                );
                return;
            }

            const data = response.data;
            const parseResult = vistarApiFetchAdsResponseSchema.safeParse(data);

            if (!parseResult.success) {
                const anError: ZodError = parseResult.error;
                Log.Error(`Vistar: Failed to parse ad: ${anError.message}`, null, __fileline);
                return;
            }

            const adResponse: VistarAPIFetchAdsResponse = parseResult.data;
            const anAd: VistarAd = {
                displayTime: 0,
                isFallback: false,
                adId: adResponse.advertisement[0].id,
                assetUrl: adResponse.advertisement[0].asset_url,
                duration: adResponse.advertisement[0].mime_type.includes('video')
                    ? adResponse.advertisement[0].length_in_seconds || maxDuration
                    : maxDuration,
                popUrl: adResponse.advertisement[0].proof_of_play_url,
                playcount: 0,
                filetype: adResponse.advertisement[0].mime_type.includes('video')
                    ? 'video'
                    : 'image',
                lastAdInLoop: false,
                expires: adResponse.advertisement[0].lease_expiry,
            };
            adQueue.enqueue(anAd);
            Log.Info(`Vistar: pushed ad with ad id ${anAd.adId}`, null, __fileline);
        }
    }

    /**
     * invokes proof of play for a Vistar ad
     * via the ad's proof of play url
     * @see {@link https://help.vistarmedia.com/hc/en-us/articles/225058628-Ad-Serving-API#pop Vistar API - Send proof of play}
     */
    static async sendProofOfPlay(ad: VistarAd): Promise<void> {
        if (ad.popUrl === '' || ad.displayTime === 0) {
            Log.Error(
                `Vistar: Failed to send proof of play for ad ${ad.adId} on player ${Signage.playerUuid}; Pop url or display time not set.`,
                null,
                __fileline
            );
            return;
        }
        const options: AxiosRequestConfig = {
            method: 'GET',
            url: ad.popUrl,
            headers: {
                'Content-Type': 'application/json',
            },
            params: {
                display_time: ad.displayTime /* epoch time (in seconds) of when ad was displayed */,
            },
        };
        try {
            await axios.request(options);
            Log.Info(
                `Vistar: Successfully sent proof of play for ad ${ad.adId} on player ${Signage.playerUuid}`,
                null,
                __fileline
            );
        } catch (error) {
            const errorMessage = this.isVistarError(error)
                ? error.response?.data.error
                : String(error);
            Log.Error(
                `Vistar: Failed to send proof of play for ad ${ad.adId} on player ${Signage.playerUuid} : ${errorMessage}`,
                null,
                __fileline
            );
        }
    }

    /**
     * Returns a boolean value indicating
     * whether the json obj passed in is empty.
     * This is to check if the response body
     * from the Vistar adrequest is empty, which
     * can occur even in the case of a valid
     * request, if no content is available at the moment
     * @see {@link https://help.vistarmedia.com/hc/en-us/articles/225058628-Ad-Serving-API#adresp Vistar API - Ad response sample}
     */
    private static isResponseEmpty(response: Record<string, never>): boolean {
        return Object.keys(response).length === 0;
    }

    private static isVistarError(err: unknown): err is VistarError {
        return axios.isAxiosError(err) && typeof err.response?.data.error === 'string';
    }
}
